import React from 'react'
import Helmet from 'react-helmet'
import { graphql, PageRendererProps } from 'gatsby'
import GatsbyImage from 'gatsby-image'
import { Block, Content, Flex, FlexSpacer } from 'gerami'
import {
  FaArrowLeft,
  FaFacebook,
  FaTelegram,
  FaTwitter,
  FaWhatsapp
} from 'react-icons/fa'
import {
  FacebookShareButton,
  TelegramShareButton,
  TwitterShareButton,
  WhatsappShareButton
} from 'react-share'

import { VideoPlaylistByIdQuery } from '../../../graphql-types'
import useSiteMetadata from '../../shared/hooks/use-site-metadata/use-site-metadata'
import App from '../app'
import Layout from '../../shared/components/layout/layout'
import { YouTubePreview } from '../cms/widgets/youtube'
import Anchor from '../../shared/components/anchor/anchor'

type VideoProps = PageRendererProps & {
  data: VideoPlaylistByIdQuery
}

const VideoPlaylist = ({ location, data }: VideoProps) => {
  const { markdownRemark: video } = data

  const siteMetadata = useSiteMetadata()
  const url = `${siteMetadata.siteUrl}${location.pathname}`
  const appName = siteMetadata?.title

  const thumbnail = video?.frontmatter?.thumbnail
  const title = video?.frontmatter?.title!
  const episodes: any[] =
    video?.frontmatter?.episodes
      ?.map(episode => (episode ? JSON.parse(episode) : null))
      .filter(episode => episode !== null) || []
  const body = video?.html || ``
  const excerpt = video?.excerpt

  return (
    <App pageRendererProps={{ location }}>
      <Helmet titleTemplate="%s | Videos">
        <title>{title}</title>
      </Helmet>

      <Layout>
        {thumbnail && (
          <GatsbyImage
            fluid={{
              aspectRatio: 4 / 3,
              src: `${thumbnail}?nf_resize=fit&w=1024`,
              srcSet: `
              ${thumbnail}?nf_resize=fit&w=1024 1024w,
              ${thumbnail}?nf_resize=fit&w=640 640w,
              ${thumbnail}?nf_resize=fit&w=320 320w
            `,
              sizes: `
              (min-width: 36em) 33.3vw,
              100vw
            `
            }}
            className="bg-whitish"
            style={{ maxHeight: `81vh` }}
          />
        )}

        <Content transparent size="4XL" className="margin-top-big">
          <Block first className="font-S padding-bottom-none">
            <Flex>
              <span className="margin-vertical-auto">
                <Anchor to="/videos">
                  <small>
                    <FaArrowLeft />
                  </small>
                  <span className="inline-block margin-left-normal">
                    Videos
                  </span>
                </Anchor>
              </span>

              <FlexSpacer />

              <span className="margin-vertical-auto fg-blackish right">
                <small className="inline-block middle margin-small margin-right-normal">
                  Share:
                </small>

                <FacebookShareButton
                  className="margin-horizontal-normal font-L middle"
                  url={url}
                  quote={`${title} | Videos – ${appName}\n\n${excerpt}`}
                  hashtag={
                    appName ? `#${appName.replace(/ /g, '')}` : undefined
                  }
                >
                  <FaFacebook />
                </FacebookShareButton>

                <TelegramShareButton
                  className="margin-horizontal-normal font-L middle"
                  url={url}
                  title={`${title} | Videos – ${appName}: ${excerpt}`}
                >
                  <FaTelegram />
                </TelegramShareButton>

                <TwitterShareButton
                  className="margin-horizontal-normal font-L middle"
                  url={url}
                  title={`${title} | Videos – ${appName}`}
                  hashtags={appName ? [appName.replace(/ /g, ``)] : undefined}
                >
                  <FaTwitter />
                </TwitterShareButton>

                <WhatsappShareButton
                  className="margin-horizontal-normal font-L middle"
                  url={url}
                  title={`${title} | Videos – ${appName}: ${excerpt}`}
                >
                  <FaWhatsapp />
                </WhatsappShareButton>
              </span>
            </Flex>
          </Block>
        </Content>

        <Content transparent size="XL" className="padding-bottom-big">
          <Block last={!episodes.length}>
            <h1 className="h0 margin-top-very-big margin-bottom-small">
              {title}
            </h1>
            <div className="subtitle">{episodes.length} Videos</div>

            <hr className="block margin-vertical-very-big" />

            <article dangerouslySetInnerHTML={{ __html: body! }} />
          </Block>

          {episodes.map((episode, i) => (
            <Block
              key={i}
              last={i >= episodes.length - 1}
              className="margin-none"
            >
              <Content className="padding-big padding-top-none margin-none">
                <YouTubePreview value={JSON.stringify(episode)} />
              </Content>
            </Block>
          ))}
        </Content>
      </Layout>
    </App>
  )
}

export default VideoPlaylist

export const pageQuery = graphql`
  query VideoPlaylistById($id: String!) {
    markdownRemark(id: { eq: $id }) {
      id
      excerpt
      frontmatter {
        isFeatured
        thumbnail
        title
        episodes
      }
      html
      excerpt
    }
  }
`
